import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { CheckCircle, XCircle, Loader2, ArrowLeft } from 'lucide-react';

const VerifyPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { verifyToken } = useAuth();
  const [status, setStatus] = useState('verifying');
  const [errorMessage, setErrorMessage] = useState('');
  const verificationAttempted = useRef(false);

  useEffect(() => {
    // Prevent multiple verification attempts (React Strict Mode / double renders)
    if (!token || verificationAttempted.current) return;
    verificationAttempted.current = true;
    
    const verify = async () => {
      try {
        const result = await verifyToken(token);
        setStatus('success');
        
        // Determine redirect destination
        let redirectPath = '/dashboard';
        
        // Check if magic link included redirect info
        if (result.redirect_dest) {
          switch (result.redirect_dest) {
            case 'chat':
              redirectPath = '/dashboard/chat';
              break;
            case 'weekly-review':
              redirectPath = '/dashboard/weekly-review';
              break;
            case 'reflections':
              redirectPath = '/dashboard/reflections';
              break;
            default:
              redirectPath = '/dashboard';
          }
          
          // Add prompt parameter if present
          if (result.prompt_id) {
            redirectPath += `?prompt=${result.prompt_id}`;
          }
        }
        
        // Redirect after brief delay
        setTimeout(() => {
          navigate(redirectPath, { replace: true });
        }, 1000);
      } catch (err) {
        setStatus('error');
        setErrorMessage(err.message || 'Invalid or expired link');
      }
    };

    verify();
  }, [token, verifyToken, navigate]);

  // If no token provided, show error immediately
  if (!token) {
    return (
      <div className="min-h-screen bg-zinc-950 flex flex-col">
        <header className="border-b border-zinc-800">
          <div className="max-w-7xl mx-auto px-4 py-4">
            <Link to="/it-leadership" className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors">
              <ArrowLeft className="w-4 h-4" />
              <span>Back to site</span>
            </Link>
          </div>
        </header>
        <main className="flex-1 flex items-center justify-center px-4">
          <div className="w-full max-w-md text-center">
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-8">
              <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-8 h-8 text-red-500" />
              </div>
              <h2 className="text-xl font-semibold text-white mb-2">Verification failed</h2>
              <p className="text-zinc-400 mb-6">No verification token provided</p>
              <Link
                to="/login"
                className="inline-block px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors"
              >
                Try signing in again
              </Link>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 flex flex-col">
      {/* Header */}
      <header className="border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <Link to="/it-leadership" className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors">
            <ArrowLeft className="w-4 h-4" />
            <span>Back to site</span>
          </Link>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 flex items-center justify-center px-4">
        <div className="w-full max-w-md text-center">
          {status === 'verifying' && (
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-8">
              <Loader2 className="w-12 h-12 text-red-500 animate-spin mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-white mb-2">Verifying your link</h2>
              <p className="text-zinc-400">Please wait...</p>
            </div>
          )}

          {status === 'success' && (
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-8">
              <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
              <h2 className="text-xl font-semibold text-white mb-2">You&apos;re signed in!</h2>
              <p className="text-zinc-400">Redirecting to your dashboard...</p>
            </div>
          )}

          {status === 'error' && (
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-8">
              <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-8 h-8 text-red-500" />
              </div>
              <h2 className="text-xl font-semibold text-white mb-2">Verification failed</h2>
              <p className="text-zinc-400 mb-6">{errorMessage}</p>
              <Link
                to="/login"
                className="inline-block px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors"
              >
                Try signing in again
              </Link>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default VerifyPage;
